import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, boolean, timestamp, jsonb, real, decimal, json, index, uniqueIndex } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const session = pgTable(
  "session",
  {
    sid: varchar("sid").primaryKey(),
    sess: json("sess").notNull(),
    expire: timestamp("expire", { precision: 6 }).notNull(),
  },
  (t) => ({
    expireIdx: index("IDX_session_expire").on(t.expire),
  }),
);

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
  email: text("email"),
  fullName: text("full_name"),
});

export const insertUserSchema = createInsertSchema(users).omit({ id: true });
export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

export const googleTokens = pgTable("google_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token"),
  expiresAt: timestamp("expires_at"),
  scope: text("scope"),
  googleEmail: text("google_email"),
});

export const insertGoogleTokenSchema = createInsertSchema(googleTokens).omit({ id: true });
export type InsertGoogleToken = z.infer<typeof insertGoogleTokenSchema>;
export type GoogleToken = typeof googleTokens.$inferSelect;

// Single-use email magic-link login tokens. Additive fallback for users in
// in-app browsers (Facebook/Instagram/TikTok webviews) where Google OAuth is
// blocked. Only the SHA-256 hash of the token is stored; the raw token lives
// only in the emailed link.
export const loginTokens = pgTable("login_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  tokenHash: text("token_hash").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  loginTokenHashIdx: index("IDX_login_tokens_hash").on(t.tokenHash),
}));

export const insertLoginTokenSchema = createInsertSchema(loginTokens).omit({ id: true, createdAt: true });
export type InsertLoginToken = z.infer<typeof insertLoginTokenSchema>;
export type LoginToken = typeof loginTokens.$inferSelect;

export const locations = pgTable("locations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id"),
  accountId: varchar("account_id"),
  name: text("name").notNull(),
  address: text("address").notNull(),
  city: text("city").notNull(),
  state: text("state").notNull(),
  zip: text("zip").notNull(),
  phone: text("phone").notNull(),
  website: text("website"),
  category: text("category").notNull(),
  lat: real("lat").notNull().default(sql`0`),
  lng: real("lng").notNull().default(sql`0`),
  avgRating: real("avg_rating").default(sql`0`),
  totalReviews: integer("total_reviews").default(sql`0`),
  listingHealth: text("listing_health").default("green"),
  status: text("status").default("active"),
  aov: decimal("aov"),
  closeRate: decimal("close_rate"),
  bookRate: decimal("book_rate"),
  showRate: decimal("show_rate"),
  profileVerifiedAt: timestamp("profile_verified_at"),
  gbpFid: text("gbp_fid"),
  gbpPlaceId: text("gbp_place_id"),
  falconGuardReportKey: text("falcon_guard_report_key"),
  gbpAccountId: text("gbp_account_id"),
  gbpLocationId: text("gbp_location_id"),
  publicVerificationToken: text("public_verification_token"),
  shareToken: text("share_token").default(sql`gen_random_uuid()`),
  gbpStatus: text("gbp_status").default("active"),
  gbpStatusUpdatedAt: timestamp("gbp_status_updated_at"),
  competitors: jsonb("competitors").default(sql`'[]'::jsonb`),
  busiestSeason: text("busiest_season"),
  brandTone: text("brand_tone"),
  autoReplyReviews: boolean("auto_reply_reviews").default(false),
  autoPostSchedule: boolean("auto_post_schedule").default(false),
  autoListingProtection: boolean("auto_listing_protection").default(false),
  autoReviewRequests: boolean("auto_review_requests").default(false),
  autoCompetitorMonitoring: boolean("auto_competitor_monitoring").default(false),
  autoWeeklyReports: boolean("auto_weekly_reports").default(false),
  autopilotEnabled: boolean("autopilot_enabled").default(false),
  socialProfiles: jsonb("social_profiles").default(sql`'{}'::jsonb`),
  logoUrl: text("logo_url"),
  featureImageUrl: text("feature_image_url"),
  gbpProductBaseline: integer("gbp_product_baseline"),
  gbpProductCurrent: integer("gbp_product_current"),
  gbpProductCheckedAt: timestamp("gbp_product_checked_at"),
  rtdManualTasks: text("rtd_manual_tasks").array().notNull().default(sql`ARRAY[]::text[]`),
  gbpProfileSnapshot: jsonb("gbp_profile_snapshot"),
  gbpProfileSnapshotAt: timestamp("gbp_profile_snapshot_at"),
});

export const insertLocationSchema = createInsertSchema(locations).omit({ id: true });
export type InsertLocation = z.infer<typeof insertLocationSchema>;
export type Location = typeof locations.$inferSelect;

export const keywords = pgTable("keywords", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  keyword: text("keyword").notNull(),
  avgRank: real("avg_rank"),
  bestRank: integer("best_rank"),
  worstRank: integer("worst_rank"),
});

export const insertKeywordSchema = createInsertSchema(keywords).omit({ id: true });
export type InsertKeyword = z.infer<typeof insertKeywordSchema>;
export type Keyword = typeof keywords.$inferSelect;

export const geogridScans = pgTable("geogrid_scans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  keywordId: varchar("keyword_id").notNull(),
  gridSize: integer("grid_size").notNull(),
  radius: text("radius").notNull(),
  creditsUsed: integer("credits_used").notNull(),
  avgRank: real("avg_rank"),
  scannedAt: timestamp("scanned_at").defaultNow(),
  gridData: jsonb("grid_data").notNull(),
  status: text("status").notNull().default("pending"),
  progress: integer("progress").default(0),
  centerLat: text("center_lat"),
  centerLng: text("center_lng"),
  solvScore: text("solv_score"),
  visibilityScore: text("visibility_score"),
  arp: text("arp"),
  atrp: text("atrp"),
  reportKey: text("report_key"),
  lfImage: text("lf_image"),
  lfHeatmap: text("lf_heatmap"),
  lfPublicUrl: text("lf_public_url"),
  uniqueCompetitors: integer("unique_competitors"),
  errorMessage: text("error_message"),
});

export const insertGeogridScanSchema = createInsertSchema(geogridScans).omit({ id: true });
export type InsertGeogridScan = z.infer<typeof insertGeogridScanSchema>;
export type GeogridScan = typeof geogridScans.$inferSelect;

export const gbpOptimizationJobs = pgTable("gbp_optimization_jobs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  userId: varchar("user_id").notNull(),
  kind: text("kind").notNull(),
  status: text("status").notNull().default("pending"),
  input: jsonb("input").notNull(),
  result: jsonb("result"),
  progressSteps: jsonb("progress_steps").notNull().default(sql`'[]'::jsonb`),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertGbpOptimizationJobSchema = createInsertSchema(gbpOptimizationJobs).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertGbpOptimizationJob = z.infer<typeof insertGbpOptimizationJobSchema>;
export type GbpOptimizationJob = typeof gbpOptimizationJobs.$inferSelect;

export const gridPoints = pgTable("grid_points", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  scanId: varchar("scan_id").notNull(),
  gridPosition: text("grid_position"),
  lat: text("lat").notNull(),
  lng: text("lng").notNull(),
  rank: integer("rank"),
  foundInMaps: boolean("found_in_maps").default(false),
  gmbListings: jsonb("gmb_listings").default(sql`'[]'::jsonb`),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertGridPointSchema = createInsertSchema(gridPoints).omit({ id: true });
export type InsertGridPoint = z.infer<typeof insertGridPointSchema>;
export type GridPoint = typeof gridPoints.$inferSelect;

export const posts = pgTable("posts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  title: text("title"),
  content: text("content").notNull(),
  postType: text("post_type").notNull().default("update"),
  status: text("status").notNull().default("draft"),
  mediaUrl: text("media_url"),
  scheduledAt: timestamp("scheduled_at"),
  publishedAt: timestamp("published_at"),
  createdAt: timestamp("created_at").defaultNow(),
  couponCode: text("coupon_code"),
  eventStartDate: timestamp("event_start_date"),
  eventEndDate: timestamp("event_end_date"),
  isRecurring: boolean("is_recurring").default(false),
  recurringSchedule: text("recurring_schedule"),
  ctaType: text("cta_type"),
  ctaUrl: text("cta_url"),
  approvalStatus: text("approval_status").default("pending"),
  autoApproveAt: timestamp("auto_approve_at"),
  approvedBy: varchar("approved_by"),
  generatedByAi: boolean("generated_by_ai").default(false),
  aiTopic: text("ai_topic"),
  tags: jsonb("tags").default(sql`'[]'::jsonb`),
  targetKeyword: text("target_keyword"),
  competitorInsight: text("competitor_insight"),
  geogridWeakness: text("geogrid_weakness"),
  entityProperties: jsonb("entity_properties"),
  kwScore: integer("kw_score"),
  imagePrompt: text("image_prompt"),
  // Atomic publish-claim marker. Set to now() when a worker claims a due post
  // for publishing; prevents the background cycle and the on-demand publish
  // path from double-posting the same scheduled post to Google. A claim older
  // than the staleness window is reclaimable so a crashed publish self-heals.
  publishAttemptAt: timestamp("publish_attempt_at"),
});

export const insertPostSchema = createInsertSchema(posts).omit({ id: true, createdAt: true });
export type InsertPost = z.infer<typeof insertPostSchema>;
export type Post = typeof posts.$inferSelect;

export const postSettings = pgTable("post_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  enabled: boolean("enabled").default(true),
  autopilot: boolean("autopilot").default(false),
  postsPerMonth: integer("posts_per_month").default(10),
  maxTopics: integer("max_topics").default(10),
  writingTone: text("writing_tone").default("professional"),
  allowEmojis: boolean("allow_emojis").default(false),
  promptModifier: text("prompt_modifier"),
  autoApproveHours: integer("auto_approve_hours").default(24),
  mediaUploads: boolean("media_uploads").default(true),
  imageSource: text("image_source").default("ai"),
  generateFromWebsite: boolean("generate_from_website").default(false),
  competitorPlaceIds: jsonb("competitor_place_ids").default(sql`'[]'::jsonb`),
  targetKeywords: jsonb("target_keywords").default(sql`'[]'::jsonb`),
  serviceAreaZips: jsonb("service_area_zips").default(sql`'[]'::jsonb`),
  brandContext: text("brand_context"),
  brandColors: text("brand_colors"),
  entityPropertyFlags: jsonb("entity_property_flags").default(sql`'{"serviceArea":true,"serviceType":true,"brandAward":false,"reviewBody":false}'::jsonb`),
  brandVoice: text("brand_voice").default("professional"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertPostSettingsSchema = createInsertSchema(postSettings).omit({ id: true, updatedAt: true });
export type InsertPostSettings = z.infer<typeof insertPostSettingsSchema>;
export type PostSettings = typeof postSettings.$inferSelect;

export const mediaFiles = pgTable("media_files", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  filename: text("filename").notNull(),
  originalName: text("original_name").notNull(),
  url: text("url").notNull(),
  mimeType: text("mime_type").notNull(),
  sizeBytes: integer("size_bytes"),
  tags: jsonb("tags").default(sql`'[]'::jsonb`),
  uploadedAt: timestamp("uploaded_at").defaultNow(),
});

export const insertMediaFileSchema = createInsertSchema(mediaFiles).omit({ id: true, uploadedAt: true });
export type InsertMediaFile = z.infer<typeof insertMediaFileSchema>;
export type MediaFile = typeof mediaFiles.$inferSelect;

export const reviews = pgTable("reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  reviewerName: text("reviewer_name").notNull(),
  reviewerAvatar: text("reviewer_avatar"),
  rating: integer("rating").notNull(),
  text: text("text"),
  reply: text("reply"),
  repliedAt: timestamp("replied_at"),
  replyStatus: text("reply_status").default("none"),
  pendingReply: text("pending_reply"),
  pendingReplyAt: timestamp("pending_reply_at"),
  isRead: boolean("is_read").default(false),
  isFlagged: boolean("is_flagged").default(false),
  postedAt: timestamp("posted_at").defaultNow(),
  gbpReviewId: text("gbp_review_id").unique(),
});

export const insertReviewSchema = createInsertSchema(reviews).omit({ id: true });
export type InsertReview = z.infer<typeof insertReviewSchema>;
export type Review = typeof reviews.$inferSelect;

export const reviewTemplates = pgTable("review_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  content: text("content").notNull(),
  category: text("category").notNull(),
});

export const insertReviewTemplateSchema = createInsertSchema(reviewTemplates).omit({ id: true });
export type InsertReviewTemplate = z.infer<typeof insertReviewTemplateSchema>;
export type ReviewTemplate = typeof reviewTemplates.$inferSelect;

export const listingChanges = pgTable("listing_changes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  field: text("field").notNull(),
  oldValue: text("old_value").notNull(),
  newValue: text("new_value").notNull(),
  severity: text("severity").notNull().default("low"),
  isReverted: boolean("is_reverted").default(false),
  detectedAt: timestamp("detected_at").defaultNow(),
});

export const insertListingChangeSchema = createInsertSchema(listingChanges).omit({ id: true });
export type InsertListingChange = z.infer<typeof insertListingChangeSchema>;
export type ListingChange = typeof listingChanges.$inferSelect;

export const notifications = pgTable("notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id"),
  userId: varchar("user_id"),
  type: text("type").notNull(),
  title: text("title").notNull(),
  message: text("message").notNull(),
  actionUrl: text("action_url"),
  actionLabel: text("action_label"),
  isRead: boolean("is_read").default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertNotificationSchema = createInsertSchema(notifications).omit({ id: true });
export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type Notification = typeof notifications.$inferSelect;

export const PLAN_TIERS = ["free", "starter", "growth", "dominator"] as const;
export type PlanTier = typeof PLAN_TIERS[number];

export const TIER_GEOGRID_CREDITS: Record<PlanTier, number> = {
  free: 338,
  starter: 1690,
  growth: 5070,
  dominator: 16900,
};

export const TIER_PRICES_USD: Record<PlanTier, number> = {
  free: 1,
  starter: 399,
  growth: 599,
  dominator: 999,
};

// New top-tier `dominator` cap is 5; existing accounts already on
// dominator with >5 locations are grandfathered (the cap check only
// blocks NEW location creation, it never archives existing ones).
export const TIER_LOCATION_LIMITS: Record<PlanTier, number> = {
  free: 1,
  starter: 1,
  growth: 3,
  dominator: 5,
};

// Display-only labels — internal tier keys remain free/starter/growth/dominator
// across the DB, Whop env vars, and webhook code paths.
export const TIER_DISPLAY_NAMES: Record<PlanTier, string> = {
  free: "Recon",
  starter: "Local Domination",
  growth: "Multi-Location Domination",
  dominator: "Market Monopoly",
};

export const TIER_TAGLINES: Record<PlanTier, string> = {
  free: "See where you stand before you attack.",
  starter: "Outrank every competitor in town.",
  growth: "Consistent leads across 2–3 locations.",
  dominator: "Own your entire service area.",
};

export type TierFeature = "autoReplyReviews" | "autoSchedulePosts" | "weeklyReports" | "aria";

// All paid tiers unlock the full automation suite. Tier differentiation is
// by location cap, scan credits, support level, and onboarding white-glove.
export const TIER_FEATURES: Record<PlanTier, Record<TierFeature, boolean>> = {
  free:      { autoReplyReviews: false, autoSchedulePosts: false, weeklyReports: false, aria: false },
  starter:   { autoReplyReviews: true,  autoSchedulePosts: true,  weeklyReports: true,  aria: true  },
  growth:    { autoReplyReviews: true,  autoSchedulePosts: true,  weeklyReports: true,  aria: true  },
  dominator: { autoReplyReviews: true,  autoSchedulePosts: true,  weeklyReports: true,  aria: true  },
};

export function tierUnlocks(tier: string | null | undefined, feature: TierFeature): boolean {
  const t = (tier ?? "free") as PlanTier;
  if (!(t in TIER_FEATURES)) return false;
  return TIER_FEATURES[t][feature];
}

export function normalizePlanTier(tier: string | null | undefined): PlanTier {
  const t = (tier ?? "free").toLowerCase();
  return (PLAN_TIERS as readonly string[]).includes(t) ? (t as PlanTier) : "free";
}

export const accounts = pgTable("accounts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ownerId: varchar("owner_id").notNull(),
  role: text("role").default("client"),
  tier: text("tier").default("free"),
  createdAt: timestamp("created_at").defaultNow(),
  leaderboardOptIn: boolean("leaderboard_opt_in").default(true),
  notifyReviews: boolean("notify_reviews").default(true),
  notifyListingChanges: boolean("notify_listing_changes").default(true),
  notifyWeeklyReport: boolean("notify_weekly_report").default(true),
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  whopUserId: text("whop_user_id"),
  whopMembershipId: text("whop_membership_id"),
  trialEndsAt: timestamp("trial_ends_at"),
  billingStatus: text("billing_status").default("trialing"),
  onboardingCompletedSteps: jsonb("onboarding_completed_steps").default(sql`'[]'::jsonb`),
  phone: text("phone"),
  smsOptIn: boolean("sms_opt_in").default(false),
  notifyEmail: boolean("notify_email").default(true),
  notifySms: boolean("notify_sms").default(false),
  geogridCredits: integer("geogrid_credits").notNull().default(338),
  geogridCreditsResetAt: timestamp("geogrid_credits_reset_at"),
  locationCapOverride: integer("location_cap_override"),
  // Terms of Service acceptance audit trail
  tosAcceptedAt: timestamp("tos_accepted_at"),
  tosVersion: text("tos_version"),
  tosAcceptedIp: text("tos_accepted_ip"),
  tosAcceptedUa: text("tos_accepted_ua"),
  // Complimentary (comped) accounts — bypass billing without going through Whop
  isComped: boolean("is_comped").default(false),
  compTier: text("comp_tier"),
  compUntil: timestamp("comp_until"),
  compNote: text("comp_note"),
  // Referral program — every account gets a unique shareable code. When a
  // new user signs up via /?ref=CODE we stamp `referredByAccountId`, then on
  // their first paid conversion we flip `referralRewarded=true` and grant
  // bonus scan credits to BOTH parties (see REFERRAL_REWARD_CREDITS).
  referralCode: text("referral_code").unique(),
  referredByAccountId: varchar("referred_by_account_id"),
  referralRewarded: boolean("referral_rewarded").default(false),
  // Marketing lead source captured at signup (e.g. "trial" for the /trial
  // $1-trial tracking link). Set once on the new-user branch of the OAuth
  // callback; existing clients re-signing in are never overwritten.
  leadSource: text("lead_source"),
  // Signup funnel tracking. `signupStage` records the furthest step a NEW
  // Google signup reached so we can remarket to people who connect their
  // Google account but never finish: "google_connected" (set on the OAuth
  // new-user branch) → "location_added" (picked a business in /setup) →
  // "trial_started" (paid the $1 trial — the completion marker). It is only
  // ever set for genuinely-new signups, so existing clients re-signing in
  // are never pulled into the funnel.
  signupStage: text("signup_stage"),
  // Set when the account converts (first paid $1-trial transition). Doubles
  // as the "signup complete" marker and gates the completion alert so Whop
  // webhook replays / renewals never re-fire it.
  signupCompletedAt: timestamp("signup_completed_at"),
  // Set when the abandoned-signup scheduler has fired its one-time
  // "unfinished signup" alert, so a stuck lead is never alerted twice.
  signupAbandonedNotifiedAt: timestamp("signup_abandoned_notified_at"),
});

// Bonus scan credits granted to both referrer and referee when the referee
// converts to a paid tier for the first time. Adjust freely.
export const REFERRAL_REWARD_CREDITS = 500;

// Paid trial: customer is charged TRIAL_INITIAL_PRICE_USD up front for
// TRIAL_DAYS, then auto-rolled onto the plan's full monthly price.
// Stripe side: createSubscriptionCheckoutSession adds a one-time
// "due today" line item (lookup_key tork_trial_fee) at this amount and
// trials the recurring tier price TRIAL_DAYS days, so the first full
// invoice matches what the UI promises.
export const TRIAL_DAYS = 3;
export const TRIAL_INITIAL_PRICE_USD = 1;

// Bump this whenever the TOS or Privacy Policy substantively changes;
// users with an older tosVersion will be re-prompted to re-accept.
export const CURRENT_TOS_VERSION = "2026-05-28";

export const insertAccountSchema = createInsertSchema(accounts).omit({ id: true, createdAt: true });
export type InsertAccount = z.infer<typeof insertAccountSchema>;
export type Account = typeof accounts.$inferSelect;

// Append-only audit log of every TOS / Privacy acceptance event. The
// accounts row above only keeps the *latest* acceptance for fast routing;
// this table is the legal source of truth for chargeback defense, GDPR
// requests, and discovery. Rows here are never updated and never deleted.
//
// Each row freezes a complete snapshot of:
//   - WHO accepted (userId, accountId, email + full name at time of click)
//   - WHAT they accepted (tosVersion + every individual acknowledgment
//     label text, so even if we rewrite the wording later, the legal
//     record shows the exact sentences they checked off)
//   - WHEN (acceptedAt timestamp, UTC)
//   - FROM WHERE (ip, userAgent, referer, sessionId)
//   - HOW (method = "checkbox_click_v1", documents = ["tos","privacy"])
export const tosAcceptanceLog = pgTable("tos_acceptance_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  accountId: varchar("account_id"),
  emailSnapshot: text("email_snapshot"),
  fullNameSnapshot: text("full_name_snapshot"),
  tosVersion: text("tos_version").notNull(),
  documents: jsonb("documents").notNull().default(sql`'["tos","privacy"]'::jsonb`),
  acknowledgments: jsonb("acknowledgments").notNull().default(sql`'[]'::jsonb`),
  acknowledgmentTexts: jsonb("acknowledgment_texts").notNull().default(sql`'[]'::jsonb`),
  acceptedAt: timestamp("accepted_at").notNull().defaultNow(),
  ip: text("ip"),
  userAgent: text("user_agent"),
  referer: text("referer"),
  sessionId: text("session_id"),
  method: text("method").notNull().default("checkbox_click_v1"),
}, (table) => ({
  userIdIdx: index("tos_acceptance_log_user_id_idx").on(table.userId),
  acceptedAtIdx: index("tos_acceptance_log_accepted_at_idx").on(table.acceptedAt),
}));

export const insertTosAcceptanceLogSchema = createInsertSchema(tosAcceptanceLog).omit({ id: true, acceptedAt: true });
export type InsertTosAcceptanceLog = z.infer<typeof insertTosAcceptanceLogSchema>;
export type TosAcceptanceLog = typeof tosAcceptanceLog.$inferSelect;

export const gbpSnapshots = pgTable("gbp_snapshots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  calls: integer("calls").default(sql`0`),
  views: integer("views").default(sql`0`),
  directions: integer("directions").default(sql`0`),
  clicks: integer("clicks").default(sql`0`),
  estimatedRevenue: decimal("estimated_revenue"),
  snappedAt: timestamp("snapped_at").defaultNow(),
});

export const insertGbpSnapshotSchema = createInsertSchema(gbpSnapshots).omit({ id: true });
export type InsertGbpSnapshot = z.infer<typeof insertGbpSnapshotSchema>;
export type GbpSnapshot = typeof gbpSnapshots.$inferSelect;

export const tokenEvents = pgTable("token_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  amount: integer("amount").notNull(),
  eventType: text("event_type").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  isBonus: boolean("is_bonus").default(false),
});

export const insertTokenEventSchema = createInsertSchema(tokenEvents).omit({ id: true, createdAt: true });
export type InsertTokenEvent = z.infer<typeof insertTokenEventSchema>;
export type TokenEvent = typeof tokenEvents.$inferSelect;

export const badges = pgTable("badges", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  badgeKey: text("badge_key").notNull(),
  earnedAt: timestamp("earned_at").defaultNow(),
});

export const insertBadgeSchema = createInsertSchema(badges).omit({ id: true });
export type InsertBadge = z.infer<typeof insertBadgeSchema>;
export type Badge = typeof badges.$inferSelect;

export const streaks = pgTable("streaks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  streakType: text("streak_type").notNull(),
  currentCount: integer("current_count").default(sql`0`),
  lastEventAt: timestamp("last_event_at"),
});

export const insertStreakSchema = createInsertSchema(streaks).omit({ id: true });
export type InsertStreak = z.infer<typeof insertStreakSchema>;
export type Streak = typeof streaks.$inferSelect;

export const listingSnapshots = pgTable("listing_snapshots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  fieldName: text("field_name").notNull(),
  expectedValue: text("expected_value").notNull(),
  currentValue: text("current_value").notNull(),
  status: text("status").notNull().default("pending"),
  detectedAt: timestamp("detected_at").defaultNow(),
  resolvedAt: timestamp("resolved_at"),
});

export const insertListingSnapshotSchema = createInsertSchema(listingSnapshots).omit({ id: true });
export type InsertListingSnapshot = z.infer<typeof insertListingSnapshotSchema>;
export type ListingSnapshot = typeof listingSnapshots.$inferSelect;

export const chatMessages = pgTable("chat_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  locationId: varchar("location_id"),
  role: text("role").notNull().default("assistant"),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertChatMessageSchema = createInsertSchema(chatMessages).omit({ id: true, createdAt: true });
export type InsertChatMessage = z.infer<typeof insertChatMessageSchema>;
export type ChatMessage = typeof chatMessages.$inferSelect;

export const monthlyReports = pgTable("monthly_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  month: text("month").notNull(),
  year: integer("year").notNull(),
  summary: text("summary"),
  metrics: jsonb("metrics").default(sql`'{}'::jsonb`),
  shareToken: text("share_token").default(sql`gen_random_uuid()`),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertMonthlyReportSchema = createInsertSchema(monthlyReports).omit({ id: true, createdAt: true });
export type InsertMonthlyReport = z.infer<typeof insertMonthlyReportSchema>;
export type MonthlyReport = typeof monthlyReports.$inferSelect;

export const assistantTasks = pgTable("assistant_tasks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  taskType: text("task_type").notNull(),
  title: text("title").notNull(),
  currentValue: text("current_value"),
  suggestedValue: text("suggested_value"),
  shortcomings: text("shortcomings"),
  benefits: text("benefits"),
  competitorsAnalyzed: jsonb("competitors_analyzed").default(sql`'[]'::jsonb`),
  status: text("status").notNull().default("pending"),
  createdAt: timestamp("created_at").defaultNow(),
  resolvedAt: timestamp("resolved_at"),
});

export const insertAssistantTaskSchema = createInsertSchema(assistantTasks).omit({ id: true, createdAt: true });
export type InsertAssistantTask = z.infer<typeof insertAssistantTaskSchema>;
export type AssistantTask = typeof assistantTasks.$inferSelect;

export const citations = pgTable("citations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  directoryName: text("directory_name").notNull(),
  tier: integer("tier").notNull().default(1),
  status: text("status").notNull().default("not_submitted"),
  listingUrl: text("listing_url"),
  submittedAt: timestamp("submitted_at"),
  lastVerifiedAt: timestamp("last_verified_at"),
});

export const insertCitationSchema = createInsertSchema(citations).omit({ id: true });
export type InsertCitation = z.infer<typeof insertCitationSchema>;
export type Citation = typeof citations.$inferSelect;

export const citationOrders = pgTable("citation_orders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  userId: varchar("user_id").notNull(),
  packageType: text("package_type").notNull(),
  status: text("status").notNull().default("pending"),
  stripeSessionId: text("stripe_session_id"),
  stripePaymentIntentId: text("stripe_payment_intent_id"),
  whopSessionId: text("whop_session_id"),
  whopMembershipId: text("whop_membership_id"),
  whopReceiptId: text("whop_receipt_id"),
  amount: integer("amount").notNull(),
  discountAmount: integer("discount_amount").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  paidAt: timestamp("paid_at"),
  paidVia: text("paid_via"),
  abandonedAt: timestamp("abandoned_at"),
  expiresAt: timestamp("expires_at"),
  installedAt: timestamp("installed_at"),
  installedBy: varchar("installed_by"),
  installNotes: text("install_notes"),
  jsonLdPayload: text("json_ld_payload"),
  customerNotes: text("customer_notes"),
});

export const insertCitationOrderSchema = createInsertSchema(citationOrders).omit({ id: true });
export type InsertCitationOrder = z.infer<typeof insertCitationOrderSchema>;
export type CitationOrder = typeof citationOrders.$inferSelect;

export const ugcVideos = pgTable("ugc_videos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  milestoneType: text("milestone_type"),
  videoUrl: text("video_url"),
  marketingConsent: boolean("marketing_consent").default(false),
  views: integer("views").default(sql`0`),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertUgcVideoSchema = createInsertSchema(ugcVideos).omit({ id: true, createdAt: true });
export type InsertUgcVideo = z.infer<typeof insertUgcVideoSchema>;
export type UgcVideo = typeof ugcVideos.$inferSelect;

export const syncJobs = pgTable("sync_jobs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  jobType: text("job_type").notNull(),
  status: text("status").notNull().default("pending"),
  errorMessage: text("error_message"),
  startedAt: timestamp("started_at").defaultNow(),
  completedAt: timestamp("completed_at"),
});

export const insertSyncJobSchema = createInsertSchema(syncJobs).omit({ id: true });
export type InsertSyncJob = z.infer<typeof insertSyncJobSchema>;
export type SyncJob = typeof syncJobs.$inferSelect;

export const placeCategories = pgTable("place_categories", {
  placeId: varchar("place_id").primaryKey(),
  category: text("category").notNull(),
  businessName: text("business_name"),
  lastVerifiedAt: timestamp("last_verified_at").defaultNow(),
});

export const insertPlaceCategorySchema = createInsertSchema(placeCategories);
export type InsertPlaceCategory = z.infer<typeof insertPlaceCategorySchema>;
export type PlaceCategory = typeof placeCategories.$inferSelect;

export const reportSettings = pgTable("report_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  dateRangeType: text("date_range_type").notNull().default("last_30"),
  startDaysAgo: integer("start_days_ago").default(30),
  endDaysAgo: integer("end_days_ago").default(0),
  compareWith: text("compare_with").notNull().default("previous_period"),
  hideNegatives: boolean("hide_negatives").default(false),
  scheduleEnabled: boolean("schedule_enabled").default(false),
  frequency: text("frequency").default("weekly"),
  dayOfWeek: text("day_of_week").default("monday"),
  scheduleTime: text("schedule_time").default("09:00"),
  emailEnabled: boolean("email_enabled").default(false),
  emailTo: text("email_to").array().default(sql`'{}'::text[]`),
  emailCc: text("email_cc").array().default(sql`'{}'::text[]`),
  emailBcc: text("email_bcc").array().default(sql`'{}'::text[]`),
  smsEnabled: boolean("sms_enabled").default(false),
  smsTo: text("sms_to").array().default(sql`'{}'::text[]`),
  runImmediately: boolean("run_immediately").default(true),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertReportSettingsSchema = createInsertSchema(reportSettings).omit({ id: true, updatedAt: true });
export type InsertReportSettings = z.infer<typeof insertReportSettingsSchema>;
export type ReportSettings = typeof reportSettings.$inferSelect;

export const notificationSends = pgTable("notification_sends", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  channel: text("channel").notNull(),
  notificationType: text("notification_type").notNull(),
  sentAt: timestamp("sent_at").defaultNow(),
});

export const insertNotificationSendSchema = createInsertSchema(notificationSends).omit({ id: true });
export type InsertNotificationSend = z.infer<typeof insertNotificationSendSchema>;
export type NotificationSend = typeof notificationSends.$inferSelect;

export const postAttributions = pgTable("post_attributions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  postId: varchar("post_id").notNull(),
  locationId: varchar("location_id").notNull(),
  scanId: varchar("scan_id"),
  keywordsBoosted: jsonb("keywords_boosted").default(sql`'[]'::jsonb`),
  visibilityChange: real("visibility_change"),
  callsAttributed: integer("calls_attributed").default(sql`0`),
  directionsAttributed: integer("directions_attributed").default(sql`0`),
  geogridCellsImproved: integer("geogrid_cells_improved").default(sql`0`),
  geogridCellsTotal: integer("geogrid_cells_total").default(sql`25`),
  preScanAvgRank: real("pre_scan_avg_rank"),
  postScanAvgRank: real("post_scan_avg_rank"),
  attributionModel: text("attribution_model").default("time_decay_7day"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPostAttributionSchema = createInsertSchema(postAttributions).omit({ id: true, createdAt: true });
export type InsertPostAttribution = z.infer<typeof insertPostAttributionSchema>;
export type PostAttribution = typeof postAttributions.$inferSelect;

export const contentSlots = pgTable("content_slots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  slotType: text("slot_type").notNull(),
  slotDay: text("slot_day").notNull(),
  weekNumber: integer("week_number").notNull(),
  year: integer("year").notNull(),
  label: text("label").notNull(),
  icon: text("icon"),
  body: text("body"),
  keywords: jsonb("keywords").default(sql`'[]'::jsonb`),
  keywordScore: integer("keyword_score").default(sql`0`),
  eatScore: integer("eat_score").default(sql`0`),
  eatSignals: jsonb("eat_signals").default(sql`'[]'::jsonb`),
  geogridTarget: text("geogrid_target"),
  geogridCells: jsonb("geogrid_cells").default(sql`'[]'::jsonb`),
  ctaType: text("cta_type"),
  imageSuggestion: text("image_suggestion"),
  status: text("status").notNull().default("ready"),
  isLocked: boolean("is_locked").default(false),
  unlockCost: integer("unlock_cost").default(sql`0`),
  postId: varchar("post_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertContentSlotSchema = createInsertSchema(contentSlots).omit({ id: true, createdAt: true });
export type InsertContentSlot = z.infer<typeof insertContentSlotSchema>;
export type ContentSlot = typeof contentSlots.$inferSelect;

export const competitorPressure = pgTable("competitor_pressure", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  competitorName: text("competitor_name").notNull(),
  postsThisWeek: integer("posts_this_week").default(sql`0`),
  postsThisMonth: integer("posts_this_month").default(sql`0`),
  lastPostDate: timestamp("last_post_date"),
  keywordsDetected: jsonb("keywords_detected").default(sql`'[]'::jsonb`),
  threatLevel: text("threat_level").default("medium"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertCompetitorPressureSchema = createInsertSchema(competitorPressure).omit({ id: true, updatedAt: true });
export type InsertCompetitorPressure = z.infer<typeof insertCompetitorPressureSchema>;
export type CompetitorPressure = typeof competitorPressure.$inferSelect;

export const awards = pgTable("awards", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  awardId: text("award_id").notNull(),
  name: text("name").notNull(),
  icon: text("icon").notNull(),
  description: text("description"),
  xp: integer("xp").notNull().default(sql`0`),
  tier: text("tier").default("standard"),
  earnedAt: timestamp("earned_at").defaultNow(),
});

export const insertAwardSchema = createInsertSchema(awards).omit({ id: true, earnedAt: true });
export type InsertAward = z.infer<typeof insertAwardSchema>;
export type Award = typeof awards.$inferSelect;

export const xpEvents = pgTable("xp_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  amount: integer("amount").notNull(),
  source: text("source").notNull(),
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertXpEventSchema = createInsertSchema(xpEvents).omit({ id: true, createdAt: true });
export type InsertXpEvent = z.infer<typeof insertXpEventSchema>;
export type XpEvent = typeof xpEvents.$inferSelect;

export const autoDominationConfig = pgTable("auto_domination_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  enabled: boolean("enabled").default(false),
  mode: text("mode").default("semi_auto"),
  postsPerWeek: integer("posts_per_week").default(sql`3`),
  tokenCostWeekly: integer("token_cost_weekly").default(sql`5`),
  lastRunAt: timestamp("last_run_at"),
  nextRunAt: timestamp("next_run_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAutoDominationConfigSchema = createInsertSchema(autoDominationConfig).omit({ id: true, createdAt: true });
export type InsertAutoDominationConfig = z.infer<typeof insertAutoDominationConfigSchema>;
export type AutoDominationConfig = typeof autoDominationConfig.$inferSelect;

export const gbpProducts = pgTable("gbp_products", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  locationId: varchar("location_id").notNull(),
  name: text("name").notNull(),
  category: text("category").notNull(),
  price: text("price"),
  priceStrategy: text("price_strategy"),
  description: text("description"),
  link: text("link"),
  imagePrompt: text("image_prompt"),
  imageUrl: text("image_url"),
  targetQuery: text("target_query"),
  secondaryQueries: text("secondary_queries").array(),
  competitorGap: text("competitor_gap"),
  charCount: integer("char_count").default(sql`0`),
  qualityScore: integer("quality_score").default(sql`0`),
  status: text("status").default("draft"),
  priority: text("priority").default("tier2"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertGbpProductSchema = createInsertSchema(gbpProducts).omit({ id: true, createdAt: true });
export type InsertGbpProduct = z.infer<typeof insertGbpProductSchema>;
export type GbpProduct = typeof gbpProducts.$inferSelect;

export const apiLogs = pgTable("api_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  service: text("service").notNull(),
  endpoint: text("endpoint").notNull(),
  method: text("method").notNull().default("GET"),
  statusCode: integer("status_code"),
  userId: varchar("user_id"),
  locationId: varchar("location_id"),
  durationMs: integer("duration_ms"),
  error: text("error"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertApiLogSchema = createInsertSchema(apiLogs).omit({ id: true, createdAt: true });
export type InsertApiLog = z.infer<typeof insertApiLogSchema>;
export type ApiLog = typeof apiLogs.$inferSelect;

export const bugReports = pgTable("bug_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  locationId: varchar("location_id"),
  title: text("title").notNull(),
  description: text("description").notNull(),
  severity: text("severity").notNull().default("medium"),
  url: text("url"),
  userAgent: text("user_agent"),
  consoleErrors: jsonb("console_errors"),
  posthogSessionId: text("posthog_session_id"),
  posthogReplayUrl: text("posthog_replay_url"),
  posthogDistinctId: text("posthog_distinct_id"),
  status: text("status").notNull().default("new"),
  adminNotes: text("admin_notes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertBugReportSchema = createInsertSchema(bugReports).omit({
  id: true,
  status: true,
  adminNotes: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertBugReport = z.infer<typeof insertBugReportSchema>;
export type BugReport = typeof bugReports.$inferSelect;

// Friends-and-family redemption links. An admin mints a shareable code
// (/redeem/<code>); anyone who signs up/logs in and visits the link gets the
// configured tier comped (isComped=true, no expiry) without going through Whop
// or entering a card. Reusable until deactivated or maxRedemptions is hit.
export const redemptionCodes = pgTable("redemption_codes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull().unique(),
  tier: text("tier").notNull().default("dominator"),
  label: text("label"),
  // null = unlimited redemptions
  maxRedemptions: integer("max_redemptions"),
  redemptionCount: integer("redemption_count").notNull().default(0),
  active: boolean("active").notNull().default(true),
  createdByAccountId: varchar("created_by_account_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertRedemptionCodeSchema = createInsertSchema(redemptionCodes).omit({
  id: true,
  redemptionCount: true,
  createdAt: true,
});
export type InsertRedemptionCode = z.infer<typeof insertRedemptionCodeSchema>;
export type RedemptionCode = typeof redemptionCodes.$inferSelect;

// One row per account that redeems a code — enforces single-use per account
// and gives admins visibility into who claimed each link.
export const redemptionUses = pgTable("redemption_uses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  codeId: varchar("code_id").notNull(),
  accountId: varchar("account_id").notNull(),
  redeemedAt: timestamp("redeemed_at").notNull().defaultNow(),
}, (t) => ({
  uniqCodeAccount: uniqueIndex("uniq_redemption_use_code_account").on(t.codeId, t.accountId),
}));

export type RedemptionUse = typeof redemptionUses.$inferSelect;

